import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppTableComponent, SelectedEvent } from '../shared/table/table.component';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { I18nMap } from '../../../models/entities/i18n-map';
import { TableTypeNames } from '../../../services/column.service';
import { ColumnMap } from '../../../models/entities/column-map';
import { PropertyManagementCompanyService } from '../../../services/property-management-company/property-management-company.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ScrollService } from '../../../services/scroll.service';
import { GrowlerService } from '../../../services/growler.service';
import { switchMap } from 'rxjs/operators';
import { Form } from '../../../models/entities/forms/form';
import { MatTableModule } from '@angular/material/table';
import { EmptyCellDirective, NumberCellDirective } from '../shared/table/table.directive';
import { DecimalPipe, I18nPluralPipe } from '@angular/common';
import { LegfiDatePipeModule } from '../../../pipes/format/legfi-date.module';
import { AvatarBlockModule } from '../shared/avatar-block/avatar-block.module';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { FormStatus } from '../../../enums/form-status.enum';
import { TableFilters } from '../../../models/entities/table-filters';
import { SidenavContentService } from '../shared/sidenav/sidenav-content/sidenav-content.service';
import { RequestFormFiltersSidenavComponent, RequestFormFiltersSidenavInput, RequestFormFiltersSidenavOutput } from './request-form-filters-sidenav/request-form-filters-sidenav.component';
import { EmptyStateModule } from '../shared/empty-state/empty-state.module';

@UntilDestroy()
@Component({
    selector: 'app-request-forms-list',
    templateUrl: './request-forms-list.component.html',
    standalone: true,
    styleUrls: ['./request-forms-list.component.scss'],
    imports: [
        AppTableComponent,
        MatSortModule,
        MatTableModule,
        EmptyCellDirective,
        I18nPluralPipe,
        LegfiDatePipeModule,
        DecimalPipe,
        NumberCellDirective,
        AvatarBlockModule,
        PageHeaderComponent,
        RouterLink,
        EmptyStateModule,
    ],
})
export class RequestFormsListComponent implements OnInit, AfterViewInit
{
    @ViewChild(AppTableComponent) tableRef: AppTableComponent<Form>;
    @ViewChild(MatSort, { static: true }) columnSort: MatSort;

    readonly organizationsMapping: I18nMap = {
        '=0': 'No organizations',
        '=1': '1 organization',
        'other': '# organizations',
    };
    readonly formStatuses = FormStatus;
    readonly tableType = TableTypeNames.PMC_FORMS;
    readonly columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'Request', exportAs: 'request', isMobile: true},
        {key: 'name', title: 'Type', exportAs: 'type'},
        {key: 'lastSubmissionDate', title: 'Last Submission', exportAs: 'lastSubmission'},
        {key: 'approversCount', title: 'Approvers', exportAs: 'approvers'},
        {key: 'openSubmissionsCount', title: 'Open Submissions', exportAs: 'openSubmissions'},
    ];

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _activatedRoute: ActivatedRoute,
            private _sidenav: SidenavContentService,
            private _router: Router,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    ngOnInit(): void {
        this._scrollService.scrollToTop();
    }

    ngAfterViewInit() {
        this.setupTable();
    }

    goToRequestForm(event: SelectedEvent<Form>) {
        return this._router.navigate(['submissions'], {
            relativeTo: this._activatedRoute,
            queryParams: { form: event.source.name, state: 'open' },
        });
    }

    openFilters(filters: TableFilters) {
        const data: RequestFormFiltersSidenavInput = {
            filters,
        };

        this._sidenav.open(RequestFormFiltersSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: RequestFormFiltersSidenavOutput) => {
            if (result?.success) {
                return this._router.navigate([], {
                    relativeTo: this._activatedRoute,
                    queryParams: {
                        ...result.filters,
                    },
                });
            }
        });
    }

    private setupTable() {
        this.tableRef.connectDataTable$.pipe(
                switchMap(() => {
                    return this._propertyManagementCompanyService.getPropertyManagementRequestForms(
                            this.tableRef.searchValue,
                            this.columnSort.active,
                            this.columnSort.direction,
                            this.tableRef.paginator.pageIndex + 1,
                            this.tableRef.paginator.pageSize,
                            this.tableRef.filters,
                    );
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.tableRef.data$.next(res),
            error: () => {
                this.tableRef.loading = false;
                this._growler.oops('Unable to load request forms');
            },
        });
    }
}
